import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, Check, Clock, X, Mail, Phone, Users, Calendar, RefreshCw, RotateCcw, UserCheck, Plus } from 'lucide-react';
import api from '../api/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const STATUS_MAP = {
  en_attente: { label: 'En attente', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  confirmee: { label: 'Confirmee', color: 'bg-green-100 text-green-800 border-green-300' },
  horaire_propose: { label: 'Horaire propose', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  refusee: { label: 'Refusee', color: 'bg-red-100 text-red-800 border-red-300' },
  arrivee: { label: 'Client arrive', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  expiree: { label: 'Expiree', color: 'bg-gray-100 text-gray-800 border-gray-300' },
};

const ReservationsPage = () => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [proposeModal, setProposeModal] = useState(null);
  const [refuseModal, setRefuseModal] = useState(null);
  const [proposeDate, setProposeDate] = useState('');
  const [proposeHeure, setProposeHeure] = useState('');
  const [proposeMessage, setProposeMessage] = useState('');
  const [refuseMotif, setRefuseMotif] = useState('');
  const [filter, setFilter] = useState('all');
  const [tables, setTables] = useState([]);
  const [arriveModal, setArriveModal] = useState(null);
  const [selectedArriveTable, setSelectedArriveTable] = useState('');
  const [manualDialog, setManualDialog] = useState(false);
  const [manualForm, setManualForm] = useState({ nom: '', prenom: '', telephone: '', email: '', couverts: 2, date: '', heure: '', message: '' });
  const [manualLoading, setManualLoading] = useState(false);

  const fetchReservations = useCallback(async () => {
    try {
      setLoading(true);
      const [resRes, tabRes] = await Promise.all([
        api.get('/reservations'),
        api.get('/tables'),
      ]);
      setReservations(resRes.data);
      setTables(tabRes.data.filter(t => t.active));
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de charger les reservations', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReservations(); }, [fetchReservations]);

  const handleConfirm = async (id) => {
    try {
      await api.put(`/reservations/${id}/confirm`);
      toast({ title: 'Reservation confirmee', description: 'Un email de confirmation a ete envoye au client' });
      fetchReservations();
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de confirmer', variant: 'destructive' });
    }
  };

  const handlePropose = async () => {
    if (!proposeDate || !proposeHeure) {
      toast({ title: 'Erreur', description: 'Veuillez renseigner la date et l\'heure', variant: 'destructive' });
      return;
    }
    try {
      await api.put(`/reservations/${proposeModal}/propose`, {
        nouvelle_date: proposeDate,
        nouvelle_heure: proposeHeure,
        message: proposeMessage,
      });
      toast({ title: 'Proposition envoyee', description: 'Un email avec le nouvel horaire a ete envoye' });
      setProposeModal(null);
      setProposeDate('');
      setProposeHeure('');
      setProposeMessage('');
      fetchReservations();
    } catch {
      toast({ title: 'Erreur', description: 'Impossible d\'envoyer la proposition', variant: 'destructive' });
    }
  };

  const handleRefuse = async () => {
    try {
      await api.put(`/reservations/${refuseModal}/refuse`, { motif: refuseMotif });
      toast({ title: 'Reservation refusee', description: 'Un email a ete envoye au client' });
      setRefuseModal(null);
      setRefuseMotif('');
      fetchReservations();
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de refuser', variant: 'destructive' });
    }
  };

  const handleRelance = async (id) => {
    try {
      await api.put(`/reservations/${id}/relance`);
      toast({ title: 'Relance envoyee', description: 'L\'email de proposition a ete renvoye au client' });
      fetchReservations();
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de relancer', variant: 'destructive' });
    }
  };

  const handleArrive = async () => {
    if (!arriveModal || !selectedArriveTable) {
      toast({ title: 'Erreur', description: 'Selectionnez une table', variant: 'destructive' });
      return;
    }
    try {
      await api.put(`/reservations/${arriveModal.id}/arrive`, { table_number: parseInt(selectedArriveTable) });
      toast({ title: 'Client arrive !', description: `Table ${selectedArriveTable} assignee` });
      setArriveModal(null);
      setSelectedArriveTable('');
      fetchReservations();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Impossible d\'assigner la table', variant: 'destructive' });
    }
  };

  const handleManualReservation = async (e) => {
    e.preventDefault();
    if (!manualForm.nom || !manualForm.prenom || !manualForm.couverts || !manualForm.date || !manualForm.heure) {
      toast({ title: 'Erreur', description: 'Remplissez les champs obligatoires', variant: 'destructive' });
      return;
    }
    setManualLoading(true);
    try {
      await api.post('/reservations/staff', {
        ...manualForm,
        couverts: parseInt(manualForm.couverts),
      });
      toast({ title: 'Reservation creee', description: `${manualForm.prenom} ${manualForm.nom} - ${manualForm.couverts} couverts` });
      setManualDialog(false);
      setManualForm({ nom: '', prenom: '', telephone: '', email: '', couverts: 2, date: '', heure: '', message: '' });
      fetchReservations();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Impossible de creer la reservation', variant: 'destructive' });
    } finally { setManualLoading(false); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  const filtered = filter === 'all'
    ? reservations.filter(r => !['arrivee', 'expiree', 'refusee'].includes(r.statut))
    : reservations.filter(r => r.statut === filter);
  const pendingCount = reservations.filter(r => r.statut === 'en_attente').length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white">
      {/* Header */}
      <div className="bg-[#3d2817] text-white py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/staff/dashboard')} className="text-white hover:bg-white/10" data-testid="back-btn">
              <ArrowLeft size={20} />
            </Button>
            <div>
              <p className="text-sm font-semibold flex items-center gap-2">
                Reservations en ligne
                {pendingCount > 0 && <Badge className="bg-amber-500 text-white">{pendingCount} en attente</Badge>}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button className="bg-green-600 hover:bg-green-700 text-white text-xs" size="sm" onClick={() => setManualDialog(true)} data-testid="new-reservation-btn">
              <Plus size={14} className="mr-1" /> Nouvelle reservation
            </Button>
            <Button variant="outline" className="text-white border-white/30 hover:bg-white/10" onClick={fetchReservations} data-testid="refresh-btn">
              <RefreshCw size={16} className="mr-2" /> Actualiser
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="status-filters">
          {[
            { key: 'all', label: 'Toutes' },
            { key: 'en_attente', label: 'En attente' },
            { key: 'confirmee', label: 'Confirmees' },
            { key: 'arrivee', label: 'Arrives' },
            { key: 'horaire_propose', label: 'Horaire propose' },
            { key: 'refusee', label: 'Refusees' },
            { key: 'expiree', label: 'Expirees' },
          ].map(f => (
            <Button key={f.key} variant={filter === f.key ? 'default' : 'outline'} size="sm"
              onClick={() => setFilter(f.key)}
              className={filter === f.key ? 'bg-[#8B0000] hover:bg-[#6B0000] text-white' : ''}
              data-testid={`filter-${f.key}`}>
              {f.label}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-stone-400">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-stone-400">Aucune reservation</div>
        ) : (
          <div className="grid gap-4">
            {filtered.map(r => {
              const st = STATUS_MAP[r.statut] || STATUS_MAP.en_attente;
              return (
                <div key={r.id} className="bg-white rounded-xl border border-stone-200 p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`reservation-${r.id}`}>
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    {/* Info client */}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-stone-800">{r.prenom} {r.nom}</h3>
                        <Badge className={`${st.color} border text-xs`}>{st.label}</Badge>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-stone-600">
                        <span className="flex items-center gap-1"><Mail size={14} /> {r.email}</span>
                        <span className="flex items-center gap-1"><Phone size={14} /> {r.telephone}</span>
                        <span className="flex items-center gap-1"><Users size={14} /> {r.couverts} couvert{r.couverts > 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm font-medium text-stone-700">
                        <Calendar size={14} />
                        <span>{formatDate(r.date)} a {r.heure}</span>
                      </div>
                      {r.message && <p className="text-xs text-stone-500 italic">"{r.message}"</p>}
                      {r.statut === 'horaire_propose' && (
                        <p className="text-xs text-blue-600 font-medium">
                          Nouvel horaire propose : {formatDate(r.nouvelle_date)} a {r.nouvelle_heure}
                        </p>
                      )}
                      {r.statut === 'refusee' && r.refus_motif && (
                        <p className="text-xs text-red-600">Motif : {r.refus_motif}</p>
                      )}
                      {r.statut === 'expiree' && r.expire_reason && (
                        <p className="text-xs text-gray-500">{r.expire_reason}</p>
                      )}
                    </div>

                    {/* Actions */}
                    {r.statut === 'en_attente' && (
                      <div className="flex gap-2 flex-shrink-0">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleConfirm(r.id)} data-testid={`confirm-${r.id}`}>
                          <Check size={16} className="mr-1" /> Confirmer
                        </Button>
                        <Button size="sm" variant="outline" className="border-blue-400 text-blue-600 hover:bg-blue-50"
                          onClick={() => { setProposeModal(r.id); setProposeDate(r.date); setProposeHeure(r.heure); }}
                          data-testid={`propose-${r.id}`}>
                          <Clock size={16} className="mr-1" /> Proposer horaire
                        </Button>
                        <Button size="sm" variant="outline" className="border-red-400 text-red-600 hover:bg-red-50"
                          onClick={() => setRefuseModal(r.id)} data-testid={`refuse-${r.id}`}>
                          <X size={16} className="mr-1" /> Refuser
                        </Button>
                      </div>
                    )}
                    {r.statut === 'confirmee' && (
                      <div className="flex gap-2 flex-shrink-0 items-center">
                        {r.assigned_table && (
                          <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-xs px-2 py-1">Table {r.assigned_table}</Badge>
                        )}
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => { setArriveModal(r); setSelectedArriveTable(''); }}
                          data-testid={`arrive-${r.id}`}>
                          <UserCheck size={16} className="mr-1" /> Client arrive
                        </Button>
                      </div>
                    )}
                    {r.statut === 'arrivee' && (
                      <div className="flex-shrink-0">
                        <Badge className="bg-emerald-500 text-white text-xs px-3 py-1">Table {r.table_number}</Badge>
                      </div>
                    )}
                    {r.statut === 'horaire_propose' && (
                      <div className="flex gap-2 flex-shrink-0">
                        <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50"
                          onClick={() => handleRelance(r.id)} data-testid={`relance-${r.id}`}>
                          <RotateCcw size={16} className="mr-1" /> Relancer
                        </Button>
                      </div>
                    )}
                    {r.statut === 'expiree' && (
                      <div className="flex-shrink-0">
                        <Badge className="bg-gray-400 text-white text-xs px-3 py-1" data-testid={`expired-badge-${r.id}`}>
                          Expiree{r.assigned_table ? ` - Table ${r.assigned_table} liberee` : ''}
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Propose Modal */}
      {proposeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="propose-modal">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-semibold text-stone-800 mb-4">Proposer un autre horaire</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-stone-600 block mb-1">Nouvelle date</label>
                <Input type="date" value={proposeDate} onChange={e => setProposeDate(e.target.value)} data-testid="propose-date" />
              </div>
              <div>
                <label className="text-sm text-stone-600 block mb-1">Nouvelle heure</label>
                <Input type="time" value={proposeHeure} onChange={e => setProposeHeure(e.target.value)} data-testid="propose-heure" />
              </div>
              <div>
                <label className="text-sm text-stone-600 block mb-1">Message (optionnel)</label>
                <textarea className="w-full border rounded-md p-2 text-sm" rows={3}
                  value={proposeMessage} onChange={e => setProposeMessage(e.target.value)}
                  placeholder="Ex: Nous sommes complets a 19h30, nous vous proposons..." data-testid="propose-message" />
              </div>
            </div>
            <div className="flex gap-2 mt-5 justify-end">
              <Button variant="outline" onClick={() => setProposeModal(null)}>Annuler</Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handlePropose} data-testid="propose-submit">
                Envoyer la proposition
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Refuse Modal */}
      {refuseModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="refuse-modal">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-semibold text-stone-800 mb-4">Refuser la reservation</h3>
            <div>
              <label className="text-sm text-stone-600 block mb-1">Motif (optionnel)</label>
              <textarea className="w-full border rounded-md p-2 text-sm" rows={3}
                value={refuseMotif} onChange={e => setRefuseMotif(e.target.value)}
                placeholder="Ex: Restaurant complet ce soir-la..." data-testid="refuse-motif" />
            </div>
            <div className="flex gap-2 mt-5 justify-end">
              <Button variant="outline" onClick={() => setRefuseModal(null)}>Annuler</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleRefuse} data-testid="refuse-submit">
                Confirmer le refus
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Arrive Modal */}
      {arriveModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="arrive-modal">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-semibold text-emerald-800 mb-2 flex items-center gap-2">
              <UserCheck size={20} /> Client arrive
            </h3>
            <p className="text-sm text-stone-600 mb-4">
              {arriveModal.prenom} {arriveModal.nom} - {arriveModal.couverts} couvert{arriveModal.couverts > 1 ? 's' : ''}
            </p>
            <div>
              <label className="text-sm text-stone-600 block mb-2 font-medium">Assigner une table :</label>
              <div className="grid grid-cols-4 gap-2">
                {tables.filter(t => !t.merged_with || t.merged_with[0] === t.table_number).map(t => {
                  const covers = t.merged_with
                    ? tables.filter(tb => t.merged_with.includes(tb.table_number)).reduce((s, tb) => s + tb.covers, 0)
                    : t.covers;
                  const isSelected = selectedArriveTable === String(t.table_number);
                  const hasEnough = covers >= arriveModal.couverts;
                  return (
                    <button key={t.table_number}
                      onClick={() => hasEnough && setSelectedArriveTable(String(t.table_number))}
                      disabled={!hasEnough}
                      className={`p-2 rounded-lg border text-center transition-all text-xs
                        ${isSelected ? 'bg-emerald-600 text-white border-emerald-600' : hasEnough ? 'bg-white border-stone-200 hover:border-emerald-400 text-stone-700' : 'bg-stone-100 text-stone-300 border-stone-100 cursor-not-allowed'}`}
                      data-testid={`arrive-table-${t.table_number}`}>
                      <div className="font-bold">T{t.table_number}</div>
                      <div className="text-[10px]">{covers} couv.</div>
                      {t.merged_with && <div className="text-[9px] text-amber-500">fusion</div>}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-2 mt-5 justify-end">
              <Button variant="outline" onClick={() => setArriveModal(null)}>Annuler</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={!selectedArriveTable}
                onClick={handleArrive} data-testid="arrive-submit">
                Confirmer l'arrivee
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Reservation Dialog */}
      <Dialog open={manualDialog} onOpenChange={setManualDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nouvelle reservation manuelle</DialogTitle></DialogHeader>
          <form onSubmit={handleManualReservation} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-stone-500 block mb-1">Prenom *</label>
                <Input value={manualForm.prenom} onChange={e => setManualForm(p => ({ ...p, prenom: e.target.value }))} placeholder="Prenom" required data-testid="manual-prenom" />
              </div>
              <div>
                <label className="text-xs text-stone-500 block mb-1">Nom *</label>
                <Input value={manualForm.nom} onChange={e => setManualForm(p => ({ ...p, nom: e.target.value }))} placeholder="Nom" required data-testid="manual-nom" />
              </div>
            </div>
            <div>
              <label className="text-xs text-stone-500 block mb-1">Telephone</label>
              <Input value={manualForm.telephone} onChange={e => setManualForm(p => ({ ...p, telephone: e.target.value }))} placeholder="0694 XX XX XX" data-testid="manual-telephone" />
            </div>
            <div>
              <label className="text-xs text-stone-500 block mb-1">Email <span className="text-stone-400">(optionnel)</span></label>
              <Input type="email" value={manualForm.email} onChange={e => setManualForm(p => ({ ...p, email: e.target.value }))} placeholder="email@example.com" data-testid="manual-email" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-stone-500 block mb-1">Couverts *</label>
                <Input type="number" min="1" max="30" value={manualForm.couverts} onChange={e => setManualForm(p => ({ ...p, couverts: e.target.value }))} required data-testid="manual-couverts" />
              </div>
              <div>
                <label className="text-xs text-stone-500 block mb-1">Date *</label>
                <Input type="date" value={manualForm.date} onChange={e => setManualForm(p => ({ ...p, date: e.target.value }))} required data-testid="manual-date" />
              </div>
              <div>
                <label className="text-xs text-stone-500 block mb-1">Heure *</label>
                <Input type="time" value={manualForm.heure} onChange={e => setManualForm(p => ({ ...p, heure: e.target.value }))} required data-testid="manual-heure" />
              </div>
            </div>
            <div>
              <label className="text-xs text-stone-500 block mb-1">Message <span className="text-stone-400">(optionnel)</span></label>
              <Input value={manualForm.message} onChange={e => setManualForm(p => ({ ...p, message: e.target.value }))} placeholder="Notes, occasion..." data-testid="manual-message" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setManualDialog(false)}>Annuler</Button>
              <Button type="submit" disabled={manualLoading} className="flex-1 bg-[#2c1810] hover:bg-[#4a2c1a]" data-testid="manual-submit">
                {manualLoading ? 'Creation...' : 'Creer la reservation'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReservationsPage;
